import * as React from 'react';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import {
  useCareerAcademyVariant,
  useIsCareerAcademyVariantLoading,
} from 'bundles/unified-career-academy/contexts/CareerAcademyVariantContext';
import { isCareerAcademyFreemiumEnabled, isCareerAcademyV2Enabled } from 'bundles/unified-career-academy/utils/helpers';
import {
  useIsProgramLoading,
  useProgram,
  useProgramMembership,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type Props = {
  children?: React.ReactNode;
};

const NotFound = () => {
  // If on server, serve a true 404 (edge will handle rendering 404 page).
  if (inServerContext) {
    throw new NotFoundError();
  } else {
    return <NotFoundPage />;
  }
};

// Ensure all conditions pass in order to show enterprise career academy.
const EnterprisePageGuard = ({ children }: Props) => {
  const isProgramLoading = useIsProgramLoading();
  const program = useProgram();
  const careerAcademyVariant = useCareerAcademyVariant();
  const isCareerAcademyVariantLoading = useIsCareerAcademyVariantLoading();
  const programMembership = useProgramMembership();

  const isValidProgram = !isProgramLoading && program;
  const isCareerAcademyFreemium = isCareerAcademyFreemiumEnabled(careerAcademyVariant);
  const shouldShowCareerAcademyForProgram =
    !isCareerAcademyVariantLoading && (isCareerAcademyV2Enabled(careerAcademyVariant) || isCareerAcademyFreemium);

  // Ensure we don't render anything until we know its ok to do so.
  if (isProgramLoading || isCareerAcademyVariantLoading || programMembership.isMembershipLoading) {
    return null;
  } else if (!isValidProgram || !shouldShowCareerAcademyForProgram) {
    return <NotFound />;
  } else {
    return <React.Fragment>{children}</React.Fragment>;
  }
};

export default EnterprisePageGuard;
